import React, { useMemo, useState } from "react";

import "./BarGraphStyles.scss";
import "../ChartFrame.scss";
import ChartTooltip from "../ChartTooltip";
import { CHART_THEME } from "../chartTheme";
import { formatTick, linearScale, truncateLabel } from "../svg/helpers";

export default function BarGraph({ domains, graphData, masterData }) {
  const [hover, setHover] = useState(null);

  const rows = useMemo(() => {
    const milRows = Array.isArray(graphData) ? graphData : [];
    const list = [];
    (domains || []).forEach((domain) => {
      const data = milRows.find((result) => result?.id == domain?.id);
      const value = parseFloat(data?.mil);
      list.push({
        id: domain?.id,
        label: domain?.domain || "",
        value: Number.isFinite(value) ? value : 0,
        // Single consistent color for all domain bars (color is decorative
        // here — the bar length carries the meaning, not the hue).
        color: "var(--cb-primary)",
      });
    });
    return list;
  }, [domains, graphData]);

  const width = 960;
  const rowH = 36;
  const margin = { top: 8, right: 40, bottom: 44, left: 150 };
  const height = margin.top + margin.bottom + rows.length * rowH;
  const plotW = width - margin.left - margin.right;
  // Maturity score is always on the 0–5 MIL scale (client requirement), so the X
  // axis is fixed at 0–5 with integer ticks regardless of the data range — matching
  // the LineGraph. Fractional MIL values sit proportionally within 0–5, so maturity
  // stays comparable across domains and assessments instead of auto-scaling per chart.
  const xMax = 5;
  const xTicks = [0, 1, 2, 3, 4, 5];
  const xDecimals = 0;
  const xScale = linearScale(0, xMax, 0, plotW);

  return (
    <div className="crr-results cb-chart-frame cb-chart-frame--bars cb-bar-chart">
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Summary of CRR results by domain"
      >
        <g transform={`translate(${margin.left},${margin.top})`}>
          {xTicks.map((t) => (
            <g key={t}>
              <line
                x1={xScale(t)}
                x2={xScale(t)}
                y1={0}
                y2={rows.length * rowH}
                stroke={CHART_THEME.grid}
                strokeDasharray="4 6"
              />
              <text
                className="cb-chart-frame__axis-label"
                x={xScale(t)}
                y={rows.length * rowH + 18}
                textAnchor="middle"
              >
                {formatTick(t, xDecimals)}
              </text>
            </g>
          ))}

          {rows.map((row, i) => {
            const y = i * rowH + 8;
            const barW = Math.max(xScale(row.value), row.value > 0 ? 6 : 0);
            const barH = 22;
            return (
              <g key={row.id || i}>
                <text
                  className="cb-chart-frame__tick-label"
                  x={-12}
                  y={y + barH / 2 + 4}
                  textAnchor="end"
                >
                  {truncateLabel(row.label, 22)}
                </text>
                <rect
                  x={0}
                  y={y}
                  width={barW}
                  height={barH}
                  rx={CHART_THEME.barRadius}
                  style={{ fill: row.color, cursor: "pointer" }}
                  onMouseEnter={(e) => {
                    const rect = e.currentTarget
                      .closest(".cb-chart-frame")
                      ?.getBoundingClientRect();
                    setHover({
                      x: e.clientX - (rect?.left || 0),
                      y: e.clientY - (rect?.top || 0),
                      label: row.label,
                      value: row.value,
                      color: row.color,
                    });
                  }}
                  onMouseLeave={() => setHover(null)}
                />
                {row.value > 0 && (
                  <text
                    className="cb-chart-frame__axis-label"
                    x={barW + 6}
                    y={y + barH / 2 + 4}
                    style={{ fill: CHART_THEME.secondary, fontWeight: 700 }}
                  >
                    {row.value}
                  </text>
                )}
              </g>
            );
          })}
        </g>
      </svg>

      <p className="cb-bar-chart__axis-title">Maturity Level Indicator</p>

      <ChartTooltip x={hover?.x || 0} y={hover?.y || 0} visible={!!hover}>
        {hover && (
          <>
            <span className="cb-chart-tooltip__title">{hover.label}</span>
            <span className="cb-chart-tooltip__row">
              <span
                className="cb-chart-tooltip__dot"
                style={{ background: hover.color }}
              />
              MIL <strong>{hover.value}</strong>
            </span>
          </>
        )}
      </ChartTooltip>
    </div>
  );
}
